import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { SectionTemplateBrField } from "components/MainSectionTemplate/SectionTemplateBrField";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { BankAccountSmallDetails, BookkeepingSmallDetails, CompanySmallDetails } from "components/SmallDetails";
import SmallDetails from "components/SmallDetails/SmallDetails";
import LifecycleSteps, { LifecycleStepsType, StepOptions } from "components/Steps/LifecycleSteps";
import { Due } from "eSign/components/Due";
import React from "react";
import { formatDaysSince, formatWrittenDayMonthYear } from "utilities";
import ViewInvoiceButtonPart from "../ViewInvoiceButtonPart";
import { CurrencyAndExchangeRate } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { CostCenterLabel } from "components/Removables/RemovableCostCenter";

interface InvoiceV2InfoSectionProps {
  stepOptions: StepOptions
  overDue: boolean
  activeStep: LifecycleStepsType
  invoice: server.dto.InvoiceV2
  brands: server.dto.BrandSmall[]
}

export const InvoiceV2InfoSection = (props: InvoiceV2InfoSectionProps) => {
  const hasOnlyOneBrand = props.brands.length === 1;

  const displayInReviewSince = props.invoice.lifeCycle.value === "review";
  const displayOutstandingSince = props.invoice.lifeCycle.value === "pending";
  const displaySendDate = props.invoice.lifeCycle.value === "completed";
  const displaySettledDate = props.invoice.lifeCycle.value === "completed";
  const displayDueDate = props.invoice.lifeCycle.value === "review" ||
    props.invoice.lifeCycle.value === "pending";

  return <DetailSectionTemplate
    isInPageEditable={false}
    onValueChanged={() => { }}
    sections={[{
      key: "invoiceDetails",
      type: "regular",
      title: RESX.Invoice.xInvoice,
      // supportArticleId: serverReference.supportArticleIds.whatIsInvoiceing,
      fields: [{
        key: "lifecycleSteps",
        label: "",
        hideLabel: true,
        compact: true,
        render: () => <LifecycleSteps
          documentType="invoice"
          pendingLabel={RESX.Financial.Outstanding}
          completedLabel={RESX.Financial.Settled}
          stepOptions={props.stepOptions}
          overDue={props.overDue}
          activeStep={props.activeStep}
          documentId={props.invoice.documentId}
        />
      }, SectionTemplateBrField("br"),
      {
        key: "brand",
        label: RESX.Branding.resxBranding,
        hidden: hasOnlyOneBrand,
        render: () => <div>{props.invoice.brand?.name}</div>,
      }, {
        key: "documentNumber",
        label: RESX.Invoice.InvoiceNumber,
        hidden: props.invoice.lifeCycle.value === "draft" || props.invoice.lifeCycle.value === "review",
        render: () => <div>{props.invoice.documentNumber}</div>
      }, {
        key: "belgianOgmNumber",
        label: RESX.Financial.OgmCode,
        hidden: !props.invoice.belgianOgmCode,
        render: () => <div>{props.invoice.belgianOgmCode}</div>
      }, {
        key: "bookkeeping",
        label: RESX.Bookkeeping.resxBookkeeping,
        render: () => <BookkeepingSmallDetails
          bookkeeping={props.invoice.bookkeeping}
        />
      }, {
        key: "currencyExchangeRate",
        label: RESX.Financial.Currency,
        render: () => <CurrencyAndExchangeRate
          bookkeeping={props.invoice.bookkeeping}
          currency={props.invoice.currency}
          exchangeRate={props.invoice.exchangeRate}
        />
      }, {
        key: "bankAccount",
        label: RESX.BankAccount.resxBankAccount,
        render: () => <BankAccountSmallDetails
          bankAccount={props.invoice.bankAccount}
        />
      }, {
        key: "company",
        label: RESX.Company.resxCompany,
        render: () => <CompanySmallDetails company={props.invoice.company} layout="multiLine" />,
      }, {
        key: "outstandingSince",
        label: RESX.Financial.Outstanding,
        hidden: !displayOutstandingSince,
        render: () => <SmallDetails
          label={formatWrittenDayMonthYear(props.invoice.sendDate)}
          subLabel={`${RESX.DateTime.Since} ${formatDaysSince(props.invoice.sendDate)}`}
        />
      }, {
        key: "sendDate",
        label: RESX.DateTime.SendDate,
        hidden: !displaySendDate,
        render: () => <SmallDetails
          label={formatWrittenDayMonthYear(props.invoice.sendDate)}
        />
      }, {
        key: "settledDate",
        label: RESX.DocumentLifeCycle.Completed,
        hidden: !displaySettledDate,
        render: () => <SmallDetails
          label={formatWrittenDayMonthYear(props.invoice.completedUtc)}
        />
      }, {
        key: "dueDate",
        label: RESX.DateTime.Due,
        hidden: !displayDueDate,
        render: () => <Due
          lifeCycle={props.invoice.lifeCycle.value}
          sendDate={props.invoice.sendDate}
          dueInDays={props.invoice.dueInDays}
        />
      }, {
        key: "inReviewSince",
        label: RESX.DocumentLifeCycle.InReview,
        hidden: !displayInReviewSince,
        render: () => <SmallDetails
          label={formatWrittenDayMonthYear(props.invoice.sendDate)}
          subLabel={`${RESX.DateTime.Since} ${formatDaysSince(props.invoice.sendDate)}`}
        />
      }, {
        key: "costCenter",
        label: RESX.CostCenter.resxCostCenter,
        hidden: !site.currentEnvironment.hasCostCenters,
        render: () => <CostCenterLabel value={props.invoice.costCenter} />,
      }, {
        key: "projectCode",
        label: RESX.Show.ProjectCode,
        isEmpty: !props.invoice.projectCode,
        render: () => <div>{props.invoice.projectCode}</div>,
        hidden: site.currentEnvironment.projectCode === "hidden",
      }, {
        key: "purchaseOrderNumber",
        label: RESX.Invoice.PONumber,
        isEmpty: !props.invoice.purchaseOrderNumber,
        hidden: !site.currentEnvironment.purchaseOrderNumber,
        render: () => <div>{props.invoice.purchaseOrderNumber}</div>,
      }, {
        key: "owner",
        label: RESX.GeneralLabels.Owner,
        render: () => <UserWithAvatar user={props.invoice.owner} />,
      }]
    }, {
      key: "previewDocument",
      type: "custom",
      render: () => <SectionPanel.Panel
        key="previewDocument"
        id="previewDocument"
      >
        <ViewInvoiceButtonPart
          documentNumber={props.invoice.documentNumber}
          label={RESX.Invoice.ViewInvoice}
          documentId={props.invoice.documentId}
        />
      </SectionPanel.Panel>
    }]}
  />
}